import React, { useState } from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import { Button } from 'shared/components';
import useErnie from 'shared/hooks/use-ernie';
import { ShareButton } from 'src/components/share-button';
import type { DutchieReferralConfig, ReferralCode } from 'src/gql/graphql';
import { useIsMounted } from 'src/hooks/use-is-mounted';
import useUI from 'src/hooks/use-ui';
import { InputRow } from 'src/payments/components';
import { DposReferralQrCode } from './dpos-referral-qrcode';
import { useGetReferralUrl } from '../use-get-referral-url';

type ReferralProps = {
  referral: ReferralCode;
  referralConfiguration?: DutchieReferralConfig;
  hideBorder?: boolean;
};

export const DposMyReferral = ({ referral, referralConfiguration, hideBorder = false }: ReferralProps): JSX.Element => {
  const showErnie = useErnie();
  const [isQrCodeVisible, setIsQrCodeVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const { isKiosk } = useUI();
  const { t } = useTranslation();

  const { code } = referral;
  const { receiverReward, senderReward, learnMoreUrl, minimumPurchaseAmount } = referralConfiguration ?? {
    receiverReward: 0,
    senderReward: 0,
    learnMoreUrl: '',
    minimumPurchaseAmount: 0,
  };
  const referralUrl = useGetReferralUrl(code ?? '0');
  const isMounted = useIsMounted();
  const isShareSupportedByBrowser = isMounted && 'share' in navigator;
  const shareButtonEnabled = isShareSupportedByBrowser && !isKiosk;

  const handleCopyReferralCode = (): void => {
    void navigator.clipboard.writeText(referralUrl ?? '');
    showErnie('Successfully copied to your clipboard!', 'success');
    setCopied(true);
  };

  return (
    <ReferralTab $hideBorder={hideBorder}>
      <HeaderText>{t('dpos.referrals.headerText', 'Share the love with friends and get rewards.')}</HeaderText>
      <SubHeaderText>
        {t(
          'dpos.referral.subHeaderText',
          `Earn ${String(senderReward)} points when your friend places their first order over $${String(
            minimumPurchaseAmount
          )}. Your friend also earns ${String(receiverReward)} points when they sign up.`
        )}
      </SubHeaderText>
      <ButtonContainer>
        <InputRow>
          <InputContainer $shareButtonEnabled={shareButtonEnabled}>
            <Input
              onClick={handleCopyReferralCode}
              $shareButtonEnabled={shareButtonEnabled}
              id='referral-url'
              data-testid='referral-url'
              variant='filled'
              value={referralUrl ?? ''}
              type='url'
              readOnly
            />
            {shareButtonEnabled && <ShareButtonStyled />}
          </InputContainer>
        </InputRow>
        <StyledButton
          onClick={() => {
            handleCopyReferralCode();
          }}
        >
          <ButtonContent>{copied ? 'Copied!' : <>Copy Link</>}</ButtonContent>
        </StyledButton>
        <StyledShowQrCodeButton
          onClick={() => {
            setIsQrCodeVisible(true);
          }}
        >
          Show QR Code
        </StyledShowQrCodeButton>
      </ButtonContainer>
      {learnMoreUrl && (
        <DisclaimersText
          onClick={(): void => {
            window.open(learnMoreUrl, '_blank');
          }}
          onKeyDown={(e): void => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              window.open(learnMoreUrl, '_blank');
            }
          }}
        >
          {' '}
          Learn more
        </DisclaimersText>
      )}
      {isQrCodeVisible && (
        <DposReferralQrCode
          onClose={() => {
            setIsQrCodeVisible(false);
          }}
          referralUrl={referralUrl ?? 'https://www.dutchie.com'}
          loading={false}
        />
      )}
    </ReferralTab>
  );
};

const StyledButton = styled(Button)`
  margin-top: 8px;
  width: 49%;
  display: inline-block;
  margin-right: 7px;
  height: 52px;
`;

const StyledShowQrCodeButton = styled(Button)`
  background-color: var(--dcm-color-surface);
  color: var(--dcm-color-primary);
  border: 2px solid;
  margin-top: 8px;
  width: 49%;
  display: inline-block;
  height: 52px;
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /*
   * Keep this fluid with a cap rather than a fixed width. Inside the mobile
   * account panel the available content box is narrower than 360px (on a
   * 393px-wide viewport it is only ~295px once the page's 24px side padding
   * and the surrounding ReferralTab's 24px padding are subtracted), so a
   * fixed 360px would overflow its container and push the referral content
   * past the right edge of the screen. This still renders at 360px wherever
   * there is room.
   */
  width: 100%;
  max-width: 360px;
  display: block;
  justify-self: center;
  margin-bottom: 10px;
`;

const ReferralTab = styled.dl<{ $hideBorder: boolean }>`
  list-style: none;
  margin: 0 auto;
  max-width: 624px;
  padding: 24px;
  border: ${({ $hideBorder }) => ($hideBorder ? 'none' : '1px solid var(--dcm-referral-border-color)')};
  border-radius: 4px;
`;

const HeaderText = styled.h2`
  font-size: 26px;
  font-weight: 700;
  justify-self: center;
  color: var(--dcm-referral-heading-color);
  margin-bottom: 8px;
  text-align: center;
`;

const SubHeaderText = styled.p`
  font-size: 16px;
  line-height: 1.5;
  justify-self: center;
  color: var(--dcm-referral-subtext-color);
  text-align: center;
  padding: 0 14px;
  max-width: 535px;
`;

const DisclaimersText = styled.button`
  font-size: 12px;
  font-weight: 400;
  justify-self: center;
  color: var(--dcm-referral-disclaimer-color);
  text-decoration: underline;
  text-decoration-color: var(--dcm-referral-disclaimer-underline-color);
  background: none;
  border: none;
  display: block;
`;

export const Input = styled.input`
  background: var(--dcm-referral-input-bg);
  border: none;
  font-size: 13px;
  width: 100%;
  color: var(--dcm-referral-input-text-color);
`;

export const InputContainer = styled.div<{ $shareButtonEnabled: boolean }>`
  display: grid;
  grid-template-columns: ${({ $shareButtonEnabled }) => ($shareButtonEnabled ? '1fr 30px' : 'none')};
  column-gap: 10px;
  align-items: center;
  padding: 10px 9px 10px 16px;
  margin: 32px 0 16px;
  border: 1px solid var(--dcm-referral-input-border-color);
  border-radius: 4px;
`;

export const ShareButtonStyled = styled(ShareButton).attrs({
  width: '16',
  height: '16',
})`
  width: 16px;
  height: 16px;
`;
